import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Activity,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Wrench,
  Code2,
  AlertTriangle,
  Undo2,
  Sparkles,
  ClipboardList,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/plano-tracking-gtm")({
  head: () => ({
    meta: [
      { title: "Admin — Tracking Meta → GTM" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-tracking-gtm">
      <PlanoTrackingGTM />
    </AdminGate>
  ),
});

function Section({
  icon: Icon,
  title,
  kicker,
  children,
  tone = "default",
}: {
  icon: typeof Activity;
  title: string;
  kicker?: string;
  children: React.ReactNode;
  tone?: "default" | "success" | "danger" | "warning";
}) {
  const border =
    tone === "success"
      ? "border-emerald-400/30"
      : tone === "danger"
      ? "border-rose-400/30"
      : tone === "warning"
      ? "border-amber-400/30"
      : "border-white/10";
  const iconTone =
    tone === "success"
      ? "text-emerald-300"
      : tone === "danger"
      ? "text-rose-300"
      : tone === "warning"
      ? "text-admin-accent"
      : "text-sky-300";
  return (
    <div className={`rounded-2xl border ${border} bg-white/[0.03] p-6 sm:p-7`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 shrink-0">
          <Icon className={`h-5 w-5 ${iconTone}`} />
        </div>
        <div>
          {kicker && (
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-semibold">
              {kicker}
            </div>
          )}
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">{title}</h3>
        </div>
      </div>
      <div className="text-sm text-white/75 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function Row({
  ok,
  children,
}: {
  ok: "removed" | "kept" | "added";
  children: React.ReactNode;
}) {
  const Icon = ok === "removed" ? XCircle : ok === "added" ? Sparkles : CheckCircle2;
  const tone =
    ok === "removed" ? "text-rose-300" : ok === "added" ? "text-sky-300" : "text-emerald-300";
  const label = ok === "removed" ? "REMOVIDO" : ok === "added" ? "NOVO" : "MANTIDO";
  return (
    <li className="flex gap-3 items-start">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone}`} />
      <div className="text-sm">
        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider mr-2 ${tone}`}>
          {label}
        </span>
        <span className="text-white/80">{children}</span>
      </div>
    </li>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-[11px] text-white bg-white/10 rounded px-1.5 py-0.5">
      {children}
    </code>
  );
}

function PlanoTrackingGTM() {
  return (
    <div className="min-h-screen text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link
            to="/admin/planos"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar aos projetos
          </Link>
          <Link
            to="/admin/leads"
            className="inline-flex items-center gap-2 text-sm text-sky-300 hover:text-sky-200 transition"
          >
            Ver /admin/leads
          </Link>
        </div>

        {/* Hero */}
        <div className="rounded-3xl border border-sky-400/25 bg-gradient-to-br from-sky-500/15 via-white/[0.03] to-transparent p-7 sm:p-9 mb-8">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-sky-400/40 bg-sky-500/15 shrink-0">
              <Activity className="h-6 w-6 text-sky-300" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-semibold">
                Projeto concluído
              </div>
              <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-white leading-tight">
                Migração de tracking do Meta para o GTM
              </h1>
              <p className="mt-3 text-white/75 leading-relaxed">
                O site <b>não dispara mais Meta Pixel nem chama a Meta Conversions API</b>. Toda a
                instrumentação passou a viver no <b>Google Tag Manager</b>, que agora é a única
                fonte de eventos para o Meta Ads. O fluxo de captura de leads, envio para o RD Station
                Marketing e criação de negócios no RD CRM permanece 100% funcional.
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 px-3 py-1 font-semibold">
                  <CheckCircle2 className="h-3 w-3" /> Captura → RD MKT: OK
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 px-3 py-1 font-semibold">
                  <CheckCircle2 className="h-3 w-3" /> Captura → RD CRM: OK
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 px-3 py-1 font-semibold">
                  <CheckCircle2 className="h-3 w-3" /> dataLayer p/ GTM: OK
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-200 px-3 py-1 font-semibold">
                  <XCircle className="h-3 w-3" /> Meta Pixel no site: desligado
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-200 px-3 py-1 font-semibold">
                  <XCircle className="h-3 w-3" /> Meta CAPI no site: desligada
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Motivação */}
        <div className="grid gap-5 mb-8">
          <Section icon={AlertTriangle} title="Por que migramos" kicker="Contexto" tone="warning">
            <p>
              Antes da migração, os eventos do Meta Ads eram gerados em três lugares ao mesmo tempo:
              Meta Pixel no navegador, Conversions API no servidor e um repasse via RD Station
              Marketing (com <Code>cf_meta_event_id</Code> / <Code>cf_meta_fbc</Code> /{" "}
              <Code>cf_meta_fbp</Code>). Mesmo com dedup por <Code>event_id</Code>, o custo de manter
              três pontos de configuração era alto e propenso a divergência entre o que o site
              enviava e o que o Meta esperava.
            </p>
            <p>
              A decisão foi concentrar 100% do tracking Meta no <b>Google Tag Manager</b>, que já é
              o padrão do time de mídia. O GTM cuida de Pixel, CAPI, geração de <Code>event_id</Code>,
              leitura de <Code>_fbc</Code>/<Code>_fbp</Code>, hashing SHA-256 de e-mail/telefone e
              dedup. O site fica com uma responsabilidade única e simples: <b>avisar o GTM</b> quando
              um lead é salvo.
            </p>
          </Section>

          <Section
            icon={Sparkles}
            title="Como o site avisa o GTM agora (contrato novo)"
            kicker="dataLayer"
            tone="success"
          >
            <p>
              Depois que o servidor confirma que o lead foi salvo, o cliente empurra dois eventos no{" "}
              <Code>window.dataLayer</Code>:
            </p>
            <div className="rounded-xl border border-white/10 bg-black/40 p-4 mt-2">
              <pre className="text-[12px] leading-relaxed text-emerald-200 overflow-x-auto">{`window.dataLayer.push({
  event: "feo_lead_saved",       // parcial (contato capturado)
  lead_source: "aulas-exp" | "ementa-site" | "pre-checkout" | "quizz-dor" | "quizz-pos" | "revista" | "aprovafeo",
  lead_area:   "orto" | "dor" | "esporte" | "geronto" | "trauma" | "prescricao" | "todas",
  lead_product:"POS-ORTO" | "POS-DOR" | ...,
  lead_email:  "email@dominio.com",
  lead_phone:  "+5511999999999",
  lead_name:   "Nome do lead"
});

window.dataLayer.push({
  event: "feo_lead_qualified",   // final (respondeu o funil)
  lead_source, lead_area, lead_product,
  lead_email, lead_phone, lead_name,
  lead_classificacao: "quente" | "morno" | "frio"
});`}</pre>
            </div>
            <p>
              O GTM configura triggers para <Code>feo_lead_saved</Code> e <Code>feo_lead_qualified</Code>{" "}
              e dispara Meta Pixel + CAPI a partir dessas variáveis. Toda a lógica de dedup, hashing
              e cookies fica dentro do GTM/servidor de tags — o site não precisa saber nada disso.
            </p>
          </Section>
        </div>

        {/* O que mudou / o que não mudou */}
        <h2 className="text-2xl font-bold text-white mt-4 mb-4 flex items-center gap-2">
          <Wrench className="h-5 w-5 text-sky-300" /> O que mudou vs. o que continua igual
        </h2>

        <div className="grid gap-5 mb-8">
          <Section icon={XCircle} title="Removido do site" kicker="Deletado / desligado" tone="danger">
            <ul className="space-y-2 mt-1">
              <Row ok="removed">
                Script base do Meta Pixel em <Code>src/routes/__root.tsx</Code> (o <Code>fbq</Code>{" "}
                não é mais carregado pelo site).
              </Row>
              <Row ok="removed">
                Tag <Code>&lt;noscript&gt;</Code> de fallback do Pixel em <Code>__root.tsx</Code>.
              </Row>
              <Row ok="removed">
                Arquivo <Code>src/lib/leads/meta-capi.server.ts</Code> deletado — nenhuma chamada
                para <Code>graph.facebook.com/events</Code> sai mais do servidor.
              </Row>
              <Row ok="removed">
                Função <Code>readMetaCookies</Code> de <Code>src/lib/leads/client-tracking.ts</Code>{" "}
                (o site não lê mais <Code>_fbc</Code>/<Code>_fbp</Code>).
              </Row>
              <Row ok="removed">
                Geração e propagação de <Code>metaEventId</Code>/<Code>event_id</Code> em todo o
                pipeline (<Code>postprocess.server.ts</Code>, <Code>rdstation.server.ts</Code>,{" "}
                <Code>capture-audit.server.ts</Code>).
              </Row>
              <Row ok="removed">
                Campos customizados <Code>cf_meta_event_id</Code>, <Code>cf_meta_fbc</Code>,{" "}
                <Code>cf_meta_fbp</Code> no payload enviado ao RD Station Marketing.
              </Row>
              <Row ok="removed">
                Campos <Code>fbc</Code>, <Code>fbp</Code>, <Code>metaEventId</Code>,{" "}
                <Code>page_url</Code>-derivados-para-Meta dos schemas Zod de todas as capturas
                (aulas-exp, ementa, pré-checkout, quizz-dor, quizz-pos, revista, aprovafeo).
              </Row>
              <Row ok="removed">
                Chamadas diretas <Code>fbq("track", "Lead")</Code> nos componentes de formulário e em{" "}
                <Code>client-tracking.ts</Code>. O <Code>pushPixelLead</Code> agora só empurra no{" "}
                <Code>dataLayer</Code>.
              </Row>
              <Row ok="removed">
                Uso das secrets <Code>META_PIXEL_ID</Code> e <Code>META_CAPI_TOKEN</Code> pelo site
                (as secrets ainda existem no ambiente, mas não são mais lidas).
              </Row>
            </ul>
          </Section>

          <Section icon={CheckCircle2} title="Preservado (não foi tocado)" kicker="Continua igual" tone="success">
            <ul className="space-y-2 mt-1">
              <Row ok="kept">
                Todos os endpoints públicos de captura em <Code>/api/public/leads/*</Code>.
              </Row>
              <Row ok="kept">
                Pipeline server-side unificado <Code>src/lib/leads/postprocess.server.ts</Code>{" "}
                — validação Zod, cálculo de tags, roteamento, gravação em <Code>public.leads</Code>.
              </Row>
              <Row ok="kept">
                Integração com <b>RD Station Marketing</b> (<Code>rdstation.server.ts</Code>) — leads
                continuam entrando com nome, e-mail, WhatsApp, tags e UTMs. Só os campos{" "}
                <Code>cf_meta_*</Code> saíram.
              </Row>
              <Row ok="kept">
                Integração com <b>RD Station CRM</b> — builder de deal em cascata, anotação
                estruturada com perguntas/respostas, round-robin de vendedoras, roteamento por
                funil (UNIVERSITÁRIOS / FUNIL-POS / SKIP), nome padronizado{" "}
                <Code>{"{FLUXO} - POS-{ÁREA} - {TEMPERATURA}"}</Code>.
              </Row>
              <Row ok="kept">
                Tabela <Code>public.leads</Code> e tabela <Code>public.lead_capture_attempts</Code>{" "}
                — schema intacto. As colunas <Code>meta_capi_status</Code>, <Code>has_fbc</Code>,{" "}
                <Code>has_fbp</Code>, <Code>meta_event_id</Code> continuam existindo por
                compatibilidade, mas passam a receber <Code>null</Code> em capturas novas.
              </Row>
              <Row ok="kept">
                Painel <Link to="/admin/leads" className="text-sky-300 underline">/admin/leads</Link>{" "}
                — tabela de leads, filtros, badges de classificação/RD MKT/RD CRM/vendedora/compras,
                CSV, documentação viva.
              </Row>
              <Row ok="kept">
                Fluxo de captura visto pelo usuário final — nenhum formulário mudou, nenhum passo
                extra foi adicionado, nada quebrou na UX.
              </Row>
              <Row ok="kept">
                Cruzamento com vendas Eduzz e coluna Compras (<Code>PRODUTO - FONTE</Code>).
              </Row>
            </ul>
          </Section>

          <Section icon={Sparkles} title="Adicionado / novo comportamento" kicker="Contrato com o GTM" tone="default">
            <ul className="space-y-2 mt-1">
              <Row ok="added">
                Eventos <Code>feo_lead_saved</Code> e <Code>feo_lead_qualified</Code> disparados no{" "}
                <Code>window.dataLayer</Code> após o servidor confirmar o lead. Contêm{" "}
                <Code>lead_source</Code>, <Code>lead_area</Code>, <Code>lead_product</Code>,{" "}
                <Code>lead_email</Code>, <Code>lead_phone</Code>, <Code>lead_name</Code>{" "}
                (+ <Code>lead_classificacao</Code> no evento qualified).
              </Row>
              <Row ok="added">
                Coluna "dataLayer (GTM)" na tabela de fluxos em{" "}
                <Link to="/admin/leads" className="text-sky-300 underline">/admin/leads</Link>{" "}
                mostrando quais capturas emitem o evento.
              </Row>
              <Row ok="added">
                Este documento — plano de referência técnico para a equipe.
              </Row>
            </ul>
          </Section>
        </div>

        {/* Arquivos tocados */}
        <h2 className="text-2xl font-bold text-white mt-4 mb-4 flex items-center gap-2">
          <Code2 className="h-5 w-5 text-sky-300" /> Arquivos tocados nesta migração
        </h2>

        <Section icon={ClipboardList} title="Mapa de arquivos" kicker="Referência">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-white/5 text-left">
                  <th className="p-2 border border-white/10">Arquivo</th>
                  <th className="p-2 border border-white/10">Mudança</th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/routes/__root.tsx</td>
                  <td className="p-2 border border-white/10">Script base do Meta Pixel + tag noscript removidos.</td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/lib/leads/meta-capi.server.ts</td>
                  <td className="p-2 border border-white/10">Arquivo deletado por completo.</td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/lib/leads/client-tracking.ts</td>
                  <td className="p-2 border border-white/10">
                    <Code>readMetaCookies</Code> removida; <Code>pushPixelLead</Code> simplificado
                    para só empurrar no <Code>dataLayer</Code>; sem <Code>event_id</Code>/<Code>fbq</Code>.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/lib/leads/postprocess.server.ts</td>
                  <td className="p-2 border border-white/10">
                    Removida geração de <Code>metaEventId</Code> e envio para CAPI; auditoria marcada
                    como <Code>meta_capi_status = "disabled"</Code>.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/lib/leads/rdstation.server.ts</td>
                  <td className="p-2 border border-white/10">
                    Removidos <Code>cf_meta_event_id</Code>, <Code>cf_meta_fbc</Code>,{" "}
                    <Code>cf_meta_fbp</Code> do payload de contato.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/lib/leads/capture-audit.server.ts</td>
                  <td className="p-2 border border-white/10">
                    Não persiste mais <Code>has_fbc</Code>/<Code>has_fbp</Code>/<Code>meta_event_id</Code>;
                    colunas permanecem no banco.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">
                    src/lib/aulas-experimentais/*, ementas/*, quizz-dor/*, quizz-pos/*, revista/*,
                    aprovafeo/*, checkout/precheckout.functions.ts
                  </td>
                  <td className="p-2 border border-white/10">
                    Schemas Zod limpos: sem <Code>fbc</Code>/<Code>fbp</Code>/<Code>metaEventId</Code>.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">
                    src/components/aulas/QualifierForm.tsx, PreCheckoutDialog.tsx, EmbedPreCheckoutView.tsx,
                    EmentaQualifierForm.tsx, RevistaQualifierForm.tsx
                  </td>
                  <td className="p-2 border border-white/10">
                    Formulários pararam de ler cookies Meta e de chamar <Code>fbq("track", "Lead")</Code>.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/routes/api/public/leads/*</td>
                  <td className="p-2 border border-white/10">
                    Handlers deixaram de referenciar campos Meta.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/lib/leads/capture-flow-catalog.ts</td>
                  <td className="p-2 border border-white/10">
                    Flag <Code>meta</Code> agora representa "emite evento no dataLayer para o GTM".
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/routes/admin.leads.tsx</td>
                  <td className="p-2 border border-white/10">
                    Seção 6 reescrita; header da coluna virou "dataLayer (GTM)"; textos de pipeline
                    atualizados.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/components/admin/LeadFunnelCard.tsx</td>
                  <td className="p-2 border border-white/10">
                    Métrica "Meta CAPI OK" substituída por "Tentativas OK"; hint atualizado.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-white/10 font-mono">src/routes/admin.plano-leads-rd-crm.tsx</td>
                  <td className="p-2 border border-white/10">
                    Timeline v13 adicionada; cards de status atualizados para refletir GTM.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Checklist da equipe */}
        <h2 className="text-2xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-300" /> Checklist para a equipe de mídia
        </h2>

        <Section icon={ClipboardList} title="O que precisa estar configurado no GTM" tone="success">
          <ol className="list-decimal pl-5 space-y-2 text-sm text-white/80">
            <li>
              Tag do Meta Pixel base ativa em todas as páginas do domínio (via GTM), com o Pixel ID
              correto e leitura automática de <Code>_fbc</Code>/<Code>_fbp</Code>.
            </li>
            <li>
              Triggers de evento personalizado no GTM para <Code>feo_lead_saved</Code> e{" "}
              <Code>feo_lead_qualified</Code>.
            </li>
            <li>
              Tag do Meta Pixel evento <b>Lead</b> disparada por esses triggers, com{" "}
              <Code>eventID</Code> = variável do GTM (gerada por ele) e parâmetros de conteúdo
              vindos de <Code>lead_source</Code>/<Code>lead_product</Code>/<Code>lead_classificacao</Code>.
            </li>
            <li>
              Tag do <b>Conversions API</b> (via server-side GTM ou tag CAPI dedicada) com o mesmo{" "}
              <Code>eventID</Code>, hashing SHA-256 de e-mail/telefone e leitura dos cookies Meta —
              tudo dentro do GTM.
            </li>
            <li>
              Confirmar no <b>Gerenciador de Eventos do Facebook</b> que Pixel + CAPI estão sendo
              deduplicados corretamente (score de correspondência e taxa de dedup).
            </li>
            <li>
              Se estiver usando a integração nativa RD Station → Meta, <b>desligar</b> — a dedup
              agora acontece exclusivamente entre Pixel e CAPI, ambos no GTM.
            </li>
          </ol>
        </Section>

        {/* Como reverter */}
        <h2 className="text-2xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <Undo2 className="h-5 w-5 text-admin-accent" /> Como reverter (se precisar)
        </h2>

        <Section
          icon={Undo2}
          title="Roteiro de rollback"
          kicker="Plano de contingência"
          tone="warning"
        >
          <p>
            Se algum dia for necessário voltar a disparar Pixel + CAPI direto do site (por exemplo,
            se o GTM ficar indisponível), o caminho é:
          </p>
          <ol className="list-decimal pl-5 space-y-2 text-sm text-white/80 mt-2">
            <li>
              Reintroduzir o script base do Meta Pixel em <Code>src/routes/__root.tsx</Code> (o
              histórico Git contém a versão original com o Pixel ID <Code>2367275056629516</Code>).
            </li>
            <li>
              Recriar <Code>src/lib/leads/meta-capi.server.ts</Code> com a chamada para{" "}
              <Code>graph.facebook.com/v18.0/{"{PIXEL_ID}"}/events</Code>, hashing SHA-256 e o token
              armazenado em <Code>META_CAPI_TOKEN</Code>.
            </li>
            <li>
              Reintroduzir <Code>readMetaCookies</Code> em <Code>client-tracking.ts</Code> e voltar a
              gerar <Code>metaEventId</Code> em <Code>postprocess.server.ts</Code>, propagando via
              schemas Zod até o RD Station (<Code>cf_meta_event_id</Code>) e a CAPI.
            </li>
            <li>
              Reativar as secrets <Code>META_PIXEL_ID</Code> e <Code>META_CAPI_TOKEN</Code> (elas
              nunca foram removidas do ambiente, apenas deixaram de ser lidas).
            </li>
            <li>
              No GTM, pausar as tags de Pixel/CAPI vinculadas a <Code>feo_lead_saved</Code>/
              <Code>feo_lead_qualified</Code> para evitar disparo duplicado durante o período
              híbrido.
            </li>
          </ol>
          <p className="text-xs text-white/60 mt-2">
            Todo o código removido está disponível no histórico do repositório. Os eventos no
            <Code>dataLayer</Code> podem continuar sendo emitidos em paralelo — eles não conflitam
            com o Pixel direto do site.
          </p>
        </Section>

        {/* Rodapé de navegação */}
        <div className="mt-10 flex flex-wrap gap-2">
          <Link
            to="/admin/leads"
            className="inline-flex items-center gap-2 rounded-full bg-sky-500/90 hover:bg-sky-400 text-[#0b0a1f] px-4 py-2 text-sm font-semibold transition"
          >
            Abrir /admin/leads
          </Link>
          <Link
            to="/admin/plano-leads-rd-crm"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white px-4 py-2 text-sm font-semibold transition"
          >
            Projeto LEADS RD/CRM
          </Link>
          <Link
            to="/admin/planos"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white px-4 py-2 text-sm font-semibold transition"
          >
            Voltar aos projetos
          </Link>
        </div>
      </div>
    </div>
  );
}
